import { Fragment, useContext, useEffect, useRef } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { classNames } from 'primereact/utils';
import { useEventListener, useMountEffect, useUnmountEffect, } from 'primereact/hooks';
import { PrimeReactContext } from 'primereact/api';

import { AppTopbarRef, LayoutState } from 'src/types/types';
import { LayoutContext } from 'src/layout/context/layoutcontext';


import AppTopbar from './AppTopbar';
import AppSidebar from './AppSidebar';

import { Categories } from './danh-muc';
import { Account } from './tai-khoan';
import { System } from './he-thong';
import QuanLyTaiLieu from './quan-ly-tai-lieu';
import Dashboard from './dashboard';
import { DocumentConfig } from './quan-ly-loai-tai-lieu';
import { TemplateDocumentConfig } from './quan-ly-loai-tai-lieu/tai-lieu-mau';
import { EmploymentContract, EmploymentContractCreate } from './hop-dong-lao-dong';
import { EmploymentContractDetail } from './hop-dong-lao-dong/EmploymentContractDetail';
export default function App() {

  const { layoutConfig, layoutState, setLayoutState } = useContext(LayoutContext);
  const { setRipple } = useContext(PrimeReactContext);
  const topbarRef = useRef<AppTopbarRef>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const [bindMenuOutsideClickListener, unbindMenuOutsideClickListener] =
    useEventListener({
      type: 'click',
      listener: (event) => {
        const isOutsideClicked = !(
          sidebarRef.current?.isSameNode(event.target as Node) ||
          sidebarRef.current?.contains(event.target as Node) ||
          topbarRef.current?.menubutton?.isSameNode(event.target as Node) ||
          topbarRef.current?.menubutton?.contains(event.target as Node)
        );

        if (isOutsideClicked) {
          hideMenu();
        }
      },
    });

  const [
    bindProfileMenuOutsideClickListener,
    unbindProfileMenuOutsideClickListener,
  ] = useEventListener({
    type: "click",
    listener: (event) => {
      const isOutsideClicked = !(
        topbarRef.current?.topbarmenu?.isSameNode(event.target as Node) ||
        topbarRef.current?.topbarmenu?.contains(event.target as Node) ||
        topbarRef.current?.topbarmenubutton?.isSameNode(event.target as Node) ||
        topbarRef.current?.topbarmenubutton?.contains(event.target as Node)
      );

      if (isOutsideClicked) {
        hideProfileMenu();
      }
    },
  });

  const hideMenu = () => {
    setLayoutState((prevLayoutState: LayoutState) => ({
      ...prevLayoutState,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));
    unbindMenuOutsideClickListener();
    unblockBodyScroll();
  };

  const hideProfileMenu = () => {
    setLayoutState((prevLayoutState: LayoutState) => ({
      ...prevLayoutState,
      profileSidebarVisible: false,
    }));
    unbindProfileMenuOutsideClickListener();
  };

  const blockBodyScroll = (): void => {
    if (document.body.classList) {
      document.body.classList.add("blocked-scroll");
    } else {
      document.body.className += " blocked-scroll";
    }
  };

  const unblockBodyScroll = (): void => {
    if (document.body.classList) {
      document.body.classList.remove("blocked-scroll");
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          "(^|\\b)" + "blocked-scroll".split(" ").join("|") + "(\\b|$)",
          "gi"
        ),
        " "
      );
    }
  };

  useMountEffect(() => {
    setRipple(layoutConfig.ripple);
  });

  useEffect(() => {
    if (layoutState.overlayMenuActive || layoutState.staticMenuMobileActive) {
      bindMenuOutsideClickListener();
    }

    layoutState.staticMenuMobileActive && blockBodyScroll();
  }, [layoutState.overlayMenuActive, layoutState.staticMenuMobileActive]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (layoutState.profileSidebarVisible) {
      bindProfileMenuOutsideClickListener();
    }
  }, [layoutState.profileSidebarVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  useUnmountEffect(() => {
    unbindMenuOutsideClickListener();
    unbindProfileMenuOutsideClickListener();
  });

  const containerClass = classNames("layout-wrapper", {
    "layout-overlay": layoutConfig.menuMode === "overlay",
    "layout-static": layoutConfig.menuMode === "static",
    "layout-static-inactive":
      layoutState.staticMenuDesktopInactive &&
      layoutConfig.menuMode === "static",
    "layout-overlay-active": layoutState.overlayMenuActive,
    "layout-mobile-active": layoutState.staticMenuMobileActive,
    "layout-dark": layoutConfig.colorScheme === "dark",
    "p-input-filled": layoutConfig.inputStyle === "filled",
    "p-ripple-disabled": !layoutConfig.ripple
  });

  return (
    <div className={containerClass}>
      <Fragment>
        <AppTopbar ref={topbarRef} />

        <div ref={sidebarRef} className="layout-sidebar">
          <AppSidebar />
        </div>

        <div className="layout-main-container">
          <div className="layout-main">
            <Routes>
              <Route path="tai-khoan/*" element={<Account />} />
              <Route path="he-thong/*" element={<System />} />
              <Route path="danh-muc/*" element={<Categories />} />
              <Route path="quan-ly-tai-lieu/*" element={<QuanLyTaiLieu />} />
              <Route path="hop-dong-lao-dong" element={<EmploymentContract />} />
              <Route path="hop-dong-lao-dong/tao-moi" element={<EmploymentContractCreate />} />
              <Route path="hop-dong-lao-dong/:sourceAgreementId/nhan-ban" element={<EmploymentContractCreate />} />
              <Route path="hop-dong-lao-dong/:agreementId/chinh-sua" element={<EmploymentContractCreate />} />
              <Route path="hop-dong-lao-dong/:agreementId" element={<EmploymentContractDetail />} />
              <Route path="quan-ly-loai-tai-lieu" element={<DocumentConfig />} />
              <Route path="quan-ly-loai-tai-lieu/tai-lieu-mau" element={<TemplateDocumentConfig />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </div>
        </div>

        <div className="layout-mask"></div>
      </Fragment>

    </div>
  );
}
